import { useState } from 'preact/hooks';
import useAppDispatch from '../../../hooks/useAppDispatch';
import useAppSelector from '../../../hooks/useAppSelector';
import { useTranslation } from '../../../hooks/useTranslation';
import Button from '../../button/Button';
import Loader from '../../loader/Loader';
import ProductPrice from '../../product/ProductPrice';
import ProductActionButtons from '../../product/action-buttons/ProductActionButtons';
import ProductImageGallery from '../../product/image-gallery/ProductImageGallery';
import ProductVariantPicker from '../../product/variant-picker/ProductVariantPicker';
import MainFeatureError from '../common/MainFeatureError';
import ProductMiniature from '../../product/miniature/ProductMiniature';
import './ProductDetail.less';
import Carousel from '../../carousel/Carousel';
import ProductCard from '../../product/card/ProductCard';

const ProductDetail = () => {
    const dispatch = useAppDispatch();
    const mainFeatureItem = useAppSelector(
        state => state.featureState.mainFeatureItem
    );
    const { loadingStatus } = useAppSelector(state => state.cartState);
    const [variantId, setVariantId] = useState<string | undefined>(undefined);
    const { t } = useTranslation();
    if (!mainFeatureItem || mainFeatureItem.featureType !== 'product-detail') {
        return null;
    }
    const { productLoadingStatus, product, similar, combinations } =
        mainFeatureItem;
    return (
        <div className={`bri-ProductDetail`}>
            {productLoadingStatus === 'success' && product && (
                <>
                    <div
                        className={`bri-ProductDetail-body-wrapper bri-SlimScroll`}
                    >
                        <div className={`bri-ProductDetail-body`}>
                            <div className={`bri-ProductDetail-images`}>
                                <ProductImageGallery product={product} />
                            </div>
                            <div className={`bri-ProductDetail-content`}>
                                <div
                                    className={`bri-ProductDetail-product-info`}
                                >
                                    <div
                                        className={`bri-ProductDetail-title bri-Headline2Font`}
                                    >
                                        {product.name}

                                        <ProductPrice
                                            product={product}
                                            className="bri-TitleBoldFont"
                                        />
                                    </div>
                                    <div
                                        className={`bri-ProductDetail-description bri-DisplayFont`}
                                    >
                                        {product.localizedDescription ??
                                            t('PRODUCT_NO_DESCRIPTION')}
                                    </div>
                                </div>
                                <ProductActionButtons product={product} />
                                <ProductVariantPicker
                                    product={product}
                                    showSingleVariant={true}
                                    onChange={(_productId, variantId) => {
                                        setVariantId(variantId);
                                    }}
                                />
                            </div>
                        </div>
                        {similar && similar.length > 0 && (
                            <div className={`bri-ProductDetail-products`}>
                                <div
                                    className={`bri-ProductDetail-products-title bri-TitleBoldFont`}
                                >
                                    {t('PRODUCT_DETAIL_SIMILAR')}
                                    <Button
                                        variant="link"
                                        endIconId="arrow-right"
                                        endIconSize="x-small"
                                        onClick={() =>
                                            dispatch.featureState.startFeature({
                                                featureType:
                                                    'product-search-similar',
                                                baseProductsIds: [
                                                    product.productId
                                                ]
                                            })
                                        }
                                    >
                                        {t(
                                            'PRODUCT_CAROUSEL_VIEW_MORE_BUTTON_LABEL'
                                        )}
                                    </Button>
                                </div>
                                <Carousel
                                    items={similar.map(product => (
                                        <ProductCard
                                            key={product.id}
                                            product={product}
                                            animationDelay={0}
                                            source="brilliant"
                                            featureType="product-search-similar"
                                        />
                                    ))}
                                />
                            </div>
                        )}
                        {combinations && combinations.length > 0 && (
                            <div className={`bri-ProductDetail-products`}>
                                <div
                                    className={`bri-ProductDetail-products-title bri-TitleBoldFont`}
                                >
                                    {t('PRODUCT_DETAIL_COMBINATIONS')}
                                    <Button
                                        variant="link"
                                        endIconId="arrow-right"
                                        endIconSize="x-small"
                                        onClick={() =>
                                            dispatch.featureState.startFeature({
                                                featureType:
                                                    'product-search-combinations',
                                                baseProductsIds: [
                                                    product.productId
                                                ]
                                            })
                                        }
                                    >
                                        {t(
                                            'PRODUCT_CAROUSEL_VIEW_MORE_BUTTON_LABEL'
                                        )}
                                    </Button>
                                </div>
                                <Carousel
                                    items={combinations.map(product => (
                                        <ProductCard
                                            key={product.id}
                                            product={product}
                                            animationDelay={0}
                                            source="brilliant"
                                            featureType="product-search-combinations"
                                        />
                                    ))}
                                />
                            </div>
                        )}
                    </div>
                    <div className="bri-ProductDetail-footer-wrapper">
                        <div className="bri-ProductDetail-footer">
                            <ProductMiniature
                                product={product}
                                size="small"
                                aspectRatio="1"
                            />
                            <div className="bri-ProductDetail-footer-content ">
                                <div className="bri-ProductDetail-footer-text bri-DisplayFont">
                                    {product.name}
                                </div>
                                <ProductPrice
                                    product={product}
                                    className="bri-ProductDetail-footer-price bri-DisplayBoldFont"
                                />
                            </div>
                            <Button
                                disabled={
                                    !variantId || loadingStatus === 'loading'
                                }
                                variant="magic"
                                size="medium"
                                className="bri-ProductDetail-add-to-cart-button"
                                onClick={() => {
                                    variantId &&
                                        void dispatch.cartState.addVariantToCart(
                                            {
                                                product,
                                                variantId,
                                                quantity: 1
                                            }
                                        );
                                }}
                            >
                                {variantId
                                    ? t('ADD_TO_CART_BUTTON_LABEL_ONE')
                                    : t('SOLD_OUT_BUTTON_LABEL')}
                            </Button>
                        </div>
                    </div>
                </>
            )}
            {productLoadingStatus === 'loading' && <Loader />}
            {productLoadingStatus === 'error' && (
                <MainFeatureError
                    onRetryClick={() =>
                        dispatch.featureState.startFeature({
                            featureType: 'product-detail',
                            productId: mainFeatureItem.productId,
                            source: 'brilliant'
                        })
                    }
                />
            )}
        </div>
    );
};

export default ProductDetail;
